/**
 * Perfil y configuración de la organización: tipo de entidad, marco legal,
 * valores por defecto de impuestos, marca y módulos opcionales.
 */
import { DEFAULT_TIME_ZONE } from '../calendar/guatemala.js';
import { DEFAULT_RISK_CATEGORY_RULES } from '../credit/delinquency.js';
import { DEFAULT_CAPACITY_POLICY } from '../credit/underwriting.js';
import { defaultSecuritySettings } from '../access/two-factor.js';
import { GUATEMALA_IVA_BP } from '../money/money.js';
import { defaultDocumentSettings } from '../documents/settings.js';
import { defaultFelSettings } from '../billing/settings.js';
import { defaultAccountingSettings } from '../accounting/accounts.js';
/** Tasa de impuesto cero para entidades exentas (p. ej. cooperativas). */
const TAX_EXEMPT_BP = 0;
/**
 * Tipos de prestamistas que operan en Guatemala. El tipo solo propone
 * valores por defecto (IVA, regulador, vocabulario); todo sigue siendo
 * editable porque el tratamiento fiscal depende de la inscripción de cada
 * entidad.
 */
export const ORGANIZATION_TYPES = [
    'microfinance_entity',
    'microfinance_ngo',
    'cooperative',
    'finance_company',
    'private_lender',
];
/** Perfil de cada tipo de organización. */
export const ORGANIZATION_TYPE_PROFILES = {
    microfinance_entity: {
        label: 'Entidad de Microfinanzas (S.A.)',
        legalFramework: 'Ley de Entidades de Microfinanzas y ONG de Microfinanzas, Decreto 25-2016',
        supervisor: 'Superintendencia de Bancos (SIB)',
        defaultTaxOnInterest: GUATEMALA_IVA_BP,
        clientNoun: 'cliente',
    },
    microfinance_ngo: {
        label: 'ONG de Microfinanzas',
        legalFramework: 'Decreto 25-2016 y Ley de ONG para el Desarrollo, Decreto 02-2003',
        supervisor: 'Registro de ONG de Microfinanzas / SIB',
        defaultTaxOnInterest: GUATEMALA_IVA_BP,
        clientNoun: 'participante',
    },
    cooperative: {
        label: 'Cooperativa de ahorro y crédito',
        legalFramework: 'Ley General de Cooperativas, Decreto 82-78',
        supervisor: 'INACOP / INGECOP',
        defaultTaxOnInterest: TAX_EXEMPT_BP,
        clientNoun: 'asociado',
    },
    finance_company: {
        label: 'Financiera / S.A. de crédito',
        legalFramework: 'Código de Comercio, Decreto 2-70',
        supervisor: 'SAT (tributario) e IVE (antilavado)',
        defaultTaxOnInterest: GUATEMALA_IVA_BP,
        clientNoun: 'cliente',
    },
    private_lender: {
        label: 'Prestamista individual o empresa de préstamos',
        legalFramework: 'Código Civil y Código de Comercio',
        supervisor: 'SAT (tributario) e IVE (antilavado)',
        defaultTaxOnInterest: GUATEMALA_IVA_BP,
        clientNoun: 'cliente',
    },
};
/** Tipo de organización por defecto al instalar Tumin. */
export const DEFAULT_ORGANIZATION_TYPE = 'microfinance_ngo';
/**
 * Tipo de cambio inicial (Q7.72 por US$, en centavos). Es solo un punto de
 * partida: la organización debe actualizarlo con la referencia del Banguat.
 */
export const DEFAULT_EXCHANGE_RATE_CENTS_PER_USD = 772;
/** Días antes del vencimiento en que se envían recordatorios por defecto. */
export const DEFAULT_REMINDER_DAYS_BEFORE = [3, 1];
/** Tono por defecto del color de acento (esmeralda, el del sistema de diseño). */
export const DEFAULT_BRAND_ACCENT_HUE = 158;
/** Nombre comercial por defecto. */
const DEFAULT_TRADE_NAME = 'Tumin';
/** NIT genérico "consumidor final" mientras la organización no registra el suyo. */
const PLACEHOLDER_NIT = 'CF';
/** Configuración inicial de una organización del tipo dado. */
export function defaultOrganizationSettings(type = DEFAULT_ORGANIZATION_TYPE) {
    return {
        legalName: 'Mi Institución de Microfinanzas',
        tradeName: DEFAULT_TRADE_NAME,
        nit: PLACEHOLDER_NIT,
        address: 'Guatemala',
        phone: '',
        email: '',
        organizationType: type,
        timeZone: DEFAULT_TIME_ZONE,
        taxOnInterest: ORGANIZATION_TYPE_PROFILES[type].defaultTaxOnInterest,
        exchangeRateCentsPerUsd: DEFAULT_EXCHANGE_RATE_CENTS_PER_USD,
        riskCategoryRules: [...DEFAULT_RISK_CATEGORY_RULES],
        capacityPolicy: DEFAULT_CAPACITY_POLICY,
        rolePermissions: {},
        extraHolidays: [],
        reminderDaysBefore: [...DEFAULT_REMINDER_DAYS_BEFORE],
        brand: { accentHue: DEFAULT_BRAND_ACCENT_HUE, logoUrl: null, shortName: DEFAULT_TRADE_NAME },
        features: {
            aiAssistant: true,
            documentOcr: true,
            pushNotifications: true,
            whatsappReminders: true,
            collateralLoans: true,
            complianceScreening: true,
        },
        documents: defaultDocumentSettings(type),
        fel: defaultFelSettings(type, DEFAULT_TRADE_NAME),
        security: defaultSecuritySettings(),
        accounting: defaultAccountingSettings(),
    };
}
//# sourceMappingURL=profile.js.map